import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BASE_PATH, Configuration } from '@kelyn-technologies/dsio-angular-lib/dsio-api';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export const OPERATIONS_BASE_PATH = new InjectionToken<string>('OPERATIONS_BASE_PATH');

export type OperationalStatus = 'ready' | 'not_found' | 'ambiguous' | 'error';

export interface OperationalSource {
  name: 'saas' | 'resources' | 'jobs' | 'cloudwatch';
  identity: Record<string, string>;
}

export interface OperationalError {
  code: string;
  message: string;
  context: Record<string, unknown>;
}

export interface OperationalDiagnostics {
  request_id?: string;
  gateway_request_id?: string;
}

export interface OperationalEnvelope<T> {
  diagnostics?: OperationalDiagnostics;
  source: OperationalSource;
  retrieved_at: string;
  status: OperationalStatus;
  data: T | null;
  error: OperationalError | null;
}

export interface ResourceGraphTopologyManifestComponent {
  component_id: string;
  native_type?: string | null;
  state?: string | null;
  root_component_id?: string | null;
  tenant_solution_scope: {
    status: 'resolved' | 'outside' | 'unavailable';
    solution_component_id: string | null;
  };
}

export interface ResourceGraphTopologyManifestOperationalData {
  tenant_id: string;
  named_root_component_id: string;
  detached_root_component_ids: string[];
  ownership_edges: {
    source_component_id: string;
    target_component_id: string;
    target_native_type?: string | null;
  }[];
  components: ResourceGraphTopologyManifestComponent[];
}

export type ResourceGraphNodeKind = 'component' | 'construct';

export interface ResourceGraphReference {
  relationship_type: 'ownership' | 'dependency';
  source_component_id: string;
  target_component_id: string;
  target_native_type: string;
  raw: Record<string, unknown>;
}

export interface ResourceGraphNode {
  id: string;
  kind: ResourceGraphNodeKind;
  component_id: string;
  tenant_id?: string;
  native_type?: string | null;
  state?: string | null;
  root_component_id?: string | null;
  tenant_solution_scope?: ResourceGraphTopologyManifestComponent['tenant_solution_scope'];
  branch_capable: boolean;
  identifiers: Record<string, unknown>;
  references: ResourceGraphReference[];
  properties: Record<string, unknown>;
  raw: Record<string, unknown>[];
}

export interface ResourceGraphOperationalData {
  component: ResourceGraphNode;
  ownership: ResourceGraphReference[];
  dependencies: ResourceGraphReference[];
  constructs: ResourceGraphNode[];
  raw: Record<string, unknown>[];
}

export interface DirectDependencySummary {
  source_reference: ResourceGraphReference;
  status: 'ready' | 'unavailable';
  target: { component_id: string; native_type?: string | null; state?: string | null; raw: Record<string, unknown>[] } | null;
  source_solution_scope: ResourceGraphTopologyManifestComponent['tenant_solution_scope'] | null;
  target_solution_scope: ResourceGraphTopologyManifestComponent['tenant_solution_scope'] | null;
  solution_relationship: 'in_solution' | 'inter_solution' | 'inter_component' | 'unavailable';
  error: { code: string; message: string } | null;
}

export interface DependentComponentsOperationalData {
  source_component_id: string;
  references: ResourceGraphReference[];
}

export interface SaasTenantSolutionOperationalRecord {
  solution_id?: string | null;
  name?: string | null;
  state?: string | null;
  raw: Record<string, unknown>;
}

export interface ResourceTenantSolutionOperationalRecord {
  component_id: string;
  solution_name?: string | null;
  state?: string | null;
  raw: Record<string, unknown>;
}

export interface TenantSolutionContext {
  match_status: 'matched' | 'unmatched_saas' | 'unmatched_resource';
  saas_record: SaasTenantSolutionOperationalRecord | null;
  resource_records: ResourceTenantSolutionOperationalRecord[];
}

export interface TenantSolutionContextsOperationalData {
  diagnostics?: OperationalDiagnostics;
  saas: OperationalEnvelope<{ records: SaasTenantSolutionOperationalRecord[] }>;
  resources: OperationalEnvelope<{ records: ResourceTenantSolutionOperationalRecord[] }>;
  contexts: TenantSolutionContext[];
}

export interface DispatchAttempt {
  key: string;
  correlation_id: string;
  target_component_id?: string | null;
  lifecycle_phase?: string | null;
  recorded_at?: string | null;
  causation_link: { source_resource_id?: string | null; source_event?: string | null };
  raw: Record<string, unknown>;
}

export interface JobsOperationalData {
  attempts: DispatchAttempt[];
  correlations: { correlation_id: string; attempts: DispatchAttempt[] }[];
  focused: { target_component_id?: string | null; lifecycle_phase?: string | null; attempts: DispatchAttempt[] }[];
}

export interface CloudWatchOperationalData {
  url: string;
  region: string;
  environment: string;
  context: Record<string, string>;
  query: string;
  log_group: string;
  start: string;
  end: string;
}

@Injectable({ providedIn: 'root' })
export class TenantOperationsService {
  constructor(
    private http: HttpClient,
    @Inject(BASE_PATH) private basePath: string,
    @Inject(OPERATIONS_BASE_PATH) private localOperationsBasePath: string,
    private configuration: Configuration
  ) {}

  private get operationsBasePath(): string {
    return this.localOperationsBasePath || this.basePath;
  }

  getResourceGraphManifest(tenantId: string): Observable<OperationalEnvelope<ResourceGraphTopologyManifestOperationalData>> {
    return this.get<OperationalEnvelope<ResourceGraphTopologyManifestOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodeURIComponent(tenantId)}/resource-graph-manifest`,
      { headers: this.operationsHeaders() }
    );
  }

  getResourceNode(
    tenantId: string,
    componentId: string
  ): Observable<OperationalEnvelope<ResourceGraphOperationalData>> {
    const encodedTenantId = encodeURIComponent(tenantId);
    const encodedComponentId = encodeURIComponent(componentId);
    return this.get<OperationalEnvelope<ResourceGraphOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodedTenantId}/resource-nodes/${encodedComponentId}`,
      { headers: this.operationsHeaders() }
    );
  }

  findDependentComponents(
    tenantId: string,
    componentId: string
  ): Observable<OperationalEnvelope<DependentComponentsOperationalData>> {
    return this.get<OperationalEnvelope<DependentComponentsOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodeURIComponent(tenantId)}/resource-nodes/${encodeURIComponent(componentId)}/dependent-components`,
      { headers: this.operationsHeaders() }
    );
  }

  getTenantSolutionContexts(tenantId: string): Observable<TenantSolutionContextsOperationalData> {
    const encodedTenantId = encodeURIComponent(tenantId);
    return this.get<TenantSolutionContextsOperationalData>(
      `${this.operationsBasePath}/operations/tenants/${encodedTenantId}/tenant-solutions`,
      { headers: this.operationsHeaders() }
    );
  }

  getJobsByResource(tenantId: string, resourceId: string): Observable<OperationalEnvelope<JobsOperationalData>> {
    return this.get<OperationalEnvelope<JobsOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodeURIComponent(tenantId)}/jobs/resources/${encodeURIComponent(resourceId)}`,
      { headers: this.operationsHeaders() }
    );
  }

  getJobsByCorrelation(tenantId: string, correlationId: string): Observable<OperationalEnvelope<JobsOperationalData>> {
    return this.get<OperationalEnvelope<JobsOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodeURIComponent(tenantId)}/jobs/correlations/${encodeURIComponent(correlationId)}`,
      { headers: this.operationsHeaders() }
    );
  }

  getCloudWatchInvestigation(
    tenantId: string,
    context: Record<string, string>
  ): Observable<OperationalEnvelope<CloudWatchOperationalData>> {
    let params = new HttpParams();
    Object.entries(context).forEach(([key, value]) => (params = params.set(key, value)));
    return this.get<OperationalEnvelope<CloudWatchOperationalData>>(
      `${this.operationsBasePath}/operations/tenants/${encodeURIComponent(tenantId)}/cloudwatch`,
      { headers: this.operationsHeaders(), params }
    );
  }

  private operationsHeaders(): HttpHeaders {
    const tenantId = this.configuration.lookupCredential('dsioTenantId');
    return tenantId ? new HttpHeaders({ 'X-DSIO-TENANT-ID': tenantId }) : new HttpHeaders();
  }

  private get<T extends OperationalEnvelope<unknown> | TenantSolutionContextsOperationalData>(
    url: string,
    options: { headers: HttpHeaders; params?: HttpParams }
  ): Observable<T> {
    return this.http.get<T>(url, { ...options, observe: 'response' }).pipe(
      tap({
        next: (response) => {
          const body: OperationalEnvelope<unknown> | TenantSolutionContextsOperationalData | null = response.body;
          if (!body) return;
          const envelopes = 'source' in body ? [body] : [body.saas, body.resources];
          for (const envelope of envelopes) {
            if (!envelope) continue;
            const unavailableResource = envelope.status === 'not_found' && envelope.source?.name === 'resources';
            if (envelope.status !== 'error' && envelope.status !== 'ambiguous' && !unavailableResource) continue;
            // Source rows, arbitrary error context, and credentials never belong in console diagnostics.
            const identity: Record<string, string> = {};
            for (const key of ['tenant_id', 'component_id', 'resource_id', 'correlation_id']) {
              const value = envelope.source?.identity?.[key];
              if (value) identity[key] = value;
            }
            const detail = {
              endpoint: url,
              http_status: response.status,
              source: envelope.source?.name,
              operational_status: envelope.status,
              error_code: envelope.error?.code,
              message: envelope.error?.message,
              ...identity,
              request_id: envelope.diagnostics?.request_id ?? body.diagnostics?.request_id ?? response.headers.get('X-Request-ID'),
              gateway_request_id: envelope.diagnostics?.gateway_request_id ?? body.diagnostics?.gateway_request_id,
            };
            if (envelope.status === 'error') console.error('[Tenant Operations] Source unavailable', detail);
            else console.warn('[Tenant Operations] Source unresolved', detail);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('[Tenant Operations] Request failed', {
            endpoint: url,
            http_status: error.status,
            message: error.message,
            request_id: error.headers?.get('X-Request-ID') ?? error.error?.diagnostics?.request_id,
            gateway_request_id: error.error?.diagnostics?.gateway_request_id,
          });
        },
      }),
      map((response) => response.body!)
    );
  }
}
